import { CreateNewFolder } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import ReloadIcon from '@mui/icons-material/Refresh';
import { Button, IconButton, MenuItem, TextField } from '@mui/material';
import {
	GridCsvExportMenuItem,
	GridExportMenuItemProps,
	GridPrintExportMenuItem,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarExport,
	GridToolbarExportContainer,
	GridToolbarFilterButton,
} from '@mui/x-data-grid';
import { observer } from 'mobx-react-lite';
import { PropsWithChildren, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridStateModel } from '~/model/model.gridstate';
import { Row } from '../view.box';

export const GridToolbarView = observer<
	PropsWithChildren<{
		onReload?: () => void;
		onCreateUrl?: string;
		gridState?: GridStateModel;
		exportEnabled?: boolean;
		rightMenu?: ReactNode;
		filterable?: boolean;
	}>
>(({ onReload, onCreateUrl, gridState, exportEnabled, children, rightMenu, filterable = true }) => {
	const nav = useNavigate();

	return (
		<GridToolbarContainer sx={{ justifyContent: 'space-between', minHeight: 64 }}>
			<Row pl={2} gap={2}>
				{filterable && gridState && (
					<TextField
						size="small"
						value={gridState?.filter ?? ''}
						onChange={gridState?.changeFilter}
						label="Filter..."
						sx={{ minWidth: 200 }}
						InputProps={{
							endAdornment: gridState?.filter && (
								<IconButton size="small" onClick={gridState.resetFilter}>
									<ClearIcon fontSize="small" />
								</IconButton>
							),
						}}
					/>
				)}
				{children}
			</Row>
			<Row>
				{onCreateUrl && (
					<Button size="small" startIcon={<CreateNewFolder />} onClick={() => nav(onCreateUrl)}>
						Create New
					</Button>
				)}
				{rightMenu}
				{exportEnabled && <GridToolbarExport />}
				<GridToolbarColumnsButton />
				<GridToolbarFilterButton />
				<GridToolbarDensitySelector />
				{onReload && (
					<Button size="small" startIcon={<ReloadIcon />} onClick={onReload}>
						Reload
					</Button>
				)}
			</Row>
		</GridToolbarContainer>
	);
});

export function GridCsvImportMenuItem(props: GridExportMenuItemProps<{}> & { onClick: () => void }) {
	const { hideMenu, onClick } = props;

	const handleClick = useCallback(() => {
		hideMenu?.();
		onClick();
	}, [hideMenu, onClick]);

	return <MenuItem onClick={handleClick}>Import from CSV</MenuItem>;
}

export function GridToolbarExportMenu({ csvOptions, printOptions, children, ...other }: PropsWithChildren<any>) {
	return (
		<GridToolbarExportContainer {...other}>
			<GridPrintExportMenuItem options={printOptions} />
			<GridCsvExportMenuItem options={csvOptions} />
			{children}
		</GridToolbarExportContainer>
	);
}
